import { useState, useEffect, useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Form, FormRenderProps } from 'react-final-form';

import { captchaSetActionAfterTokenLoad, captchaSetIsActive } from 'CorePackage/CaptchaModule/actions/captchaActions';
import { getCaptchaToken } from 'CorePackage/CaptchaModule/selectors/captchaSelectors';
import { coreHideNotices } from 'CorePackage/MainModule/actions/coreActions';
import { composeValidators } from 'CorePackage/MainModule/common/composeValidators';
import { FeedbackAgreementModal } from 'CorePackage/MainModule/components/FeedbackAgreementModal';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { makeEmailValidation } from 'CorePackage/MainModule/validators/makeEmailValidation';
import { makeMaxCharactersValidator } from 'CorePackage/MainModule/validators/makeMaxCharactersValidator';
import { makePhoneValidation } from 'CorePackage/MainModule/validators/makePhoneValidation';
import { makeRequired } from 'CorePackage/MainModule/validators/makeRequired';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { UploadInput } from 'UIPackage/MainModule/components/UploadInput';
import { UploadStatus } from 'UIPackage/MainModule/components/UploadStatus';

import { IFeedbackForm } from '../interfaces/IFeedbackForm';
import { feedbackPost, feedbackSetFiles, feedbackSetForm } from '../actions/feedbackActions';
import styles from './PublicFeedback.module.scss';
import { TextInput } from '../../../UIPackage/MainModule/components/TextInput';
import { getFeedbackKnmData } from '../reducers/feedbackReducer';
import { IFeedbackKnmData } from '../interfaces/IFeedbackKnmData';
import { parseErknmValue } from '../../../PassportKnmPackage/MainModule/common/parseErknmValue';
import { formatDate } from '../../../CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_MINUTE } from '../../../CorePackage/MainModule/constants/dateFormat';

interface IPublicFeedbackFormStoreProps {
  captchaToken: string | null;
  knmData: IFeedbackKnmData | undefined;
}

const PublicFeedbackFormWithoutStore = ({ captchaToken, knmData }: IPublicFeedbackFormStoreProps) => {
  const dispatch = useDispatch();
  const formRef = useRef<FormRenderProps<IFeedbackForm>['form']>();
  useEffect(() => {
    window.scroll(0, 0);
    return () => {
      dispatch(feedbackSetFiles([]));
      dispatch(coreHideNotices());
    };
  }, [dispatch]);

  const [error, setError] = useState('');
  const [isAgreementOpen, setIsAgreementOpen] = useState(false);

  const getLink = () => {
    return `${window.location.origin}/portal/public-knm/${knmData?.id}`;
  };

  const setTextDescription = (text: string) => {
    if (knmData && knmData.kindKnm) {
      return `Номер паспорта КНМ: ${knmData.erpId}\nСтатус паспорта КНМ: ${knmData.status}\nСтатус подписания КНМ: ${
        knmData.signed ? 'Подписано' : 'Не подписано'
      }\nХарактер КНМ: ${knmData.typeName}\nВид КНМ: ${
        parseErknmValue<{ title?: string }>(knmData.kindKnm.value)?.title
      }\nНеобходимость согласования: ${
        knmData.approveRequired || 'Не требуется'
      }\nСсылка на паспорт КНМ: ${getLink()}\n Пользователь: \n***\n${text}`;
    }

    return text;
  };

  const onSubmitCallback = (values: IFeedbackForm) => {
    dispatch(captchaSetActionAfterTokenLoad(feedbackPost(values, setTextDescription(values.text), formRef.current)));
    if (!captchaToken) {
      dispatch(captchaSetIsActive(true));
    } else {
      dispatch(feedbackPost(values, setTextDescription(values.text), formRef.current)());
    }
  };

  const onModalClose = () => setIsAgreementOpen(false);
  const onModalOpen = () => setIsAgreementOpen(true);

  const options = [
    { value: 1, label: 'Контролирующий орган', id: 'SUPERVISORY_AUTHORITY' },
    { value: 2, label: 'Орган прокуратуры', id: 'PROSECUTORS_OFFICE' },
    { value: 3, label: 'Юридическое лицо', id: 'LEGAL_ENTITY' },
    { value: 4, label: 'Иное', id: 'OTHER' },
  ];

  const renderForm = ({ handleSubmit, form }: FormRenderProps<IFeedbackForm>) => {
    formRef.current = form;

    if (knmData) {
      const currentDateTime = formatDate({
        date: new Date(),
        dateFormat: DATE_FORMAT_LONG_MINUTE,
      });

      form.change('topic', `Проблема с КНМ ${knmData.erpId}_${currentDateTime}`);
      form.change('knmId', knmData.id);
    }

    dispatch(feedbackSetForm(form));

    return (
      <>
        <form className={styles.FeedbackForm} onSubmit={handleSubmit}>
          <div className={styles.Group}>
            <h4 className={styles.FeedbackFormLabel}>Сведения об отправителе</h4>
            <TextInputField
              className={styles.FieldLastName}
              name="lastName"
              placeholder="Фамилия"
              validate={composeValidators(
                makeRequired('Поле не может быть пустым'),
                makeMaxCharactersValidator(256, 'Превышена допустимая длина строки'),
              )}
            />
            <TextInputField
              className={styles.FieldFirstName}
              placeholder="Имя"
              name="firstName"
              validate={composeValidators(
                makeRequired('Поле не может быть пустым'),
                makeMaxCharactersValidator(256, 'Превышена допустимая длина строки'),
              )}
            />
            <TextInputField
              className={styles.FieldMiddleName}
              placeholder="Отчество"
              name="patronymic"
              validate={makeMaxCharactersValidator(256, 'Превышена допустимая длина строки')}
            />
            <TextInputField
              className={styles.FieldPhone}
              placeholder="Телефон"
              name="phone"
              isPhone
              validate={composeValidators(
                makePhoneValidation('Введены недопустимые символы'),
                makeMaxCharactersValidator(256, 'Превышена допустимая длина строки'),
              )}
            />
            <TextInputField
              className={styles.FieldEmail}
              placeholder="Электронная почта"
              name="email"
              validate={composeValidators(
                makeRequired('Поле не может быть пустым'),
                makeMaxCharactersValidator(256, 'Превышена допустимая длина строки'),
                makeEmailValidation('Введены недопустимые символы'),
              )}
            />
            <SelectInputField
              className={styles.FieldApplicant}
              placeholder="Тип заявителя"
              name="applicantType"
              options={options}
              validate={makeRequired('Поле не может быть пустым')}
            />
            <TextInputField
              className={styles.FieldOrganization}
              placeholder="Наименование организации"
              name="organizationName"
              validate={composeValidators(
                makeRequired('Поле не может быть пустым'),
                makeMaxCharactersValidator(1024, 'Превышена допустимая длина строки'),
              )}
            />
          </div>

          <div>
            <TextInputField
              className={styles.Position}
              name="position"
              placeholder="Должность"
              validate={composeValidators(
                makeRequired('Поле не может быть пустым'),
                makeMaxCharactersValidator(600, 'Превышена допустимая длина строки'),
              )}
            />
          </div>

          <div className={styles.Group}>
            <h4 className={styles.FeedbackFormLabel}>Содержание обращения</h4>
            {knmData && (
              <div className={styles.Group}>
                <h4 className={styles.FeedbackFormLabel}>Номер паспорта КНМ</h4>
                <TextInput name="knmErpId" disabled={true} className={styles.FiledKnmGroup} value={knmData.erpId} />

                <h4 className={styles.FeedbackFormLabel}>Статус КНМ</h4>
                <TextInput name="status" disabled={true} className={styles.FiledKnmGroup} value={knmData.status} />

                <h4 className={styles.FeedbackFormLabel}>Характер КНМ</h4>
                <TextInput name="typeErknm" disabled={true} className={styles.FiledKnmGroup} value={knmData.typeName} />

                <h4 className={styles.FeedbackFormLabel}>Статус подписания</h4>
                <TextInput
                  name="typeErknm"
                  disabled={true}
                  className={styles.FiledKnmGroup}
                  value={knmData.signed ? 'Подписано' : 'Не подписано'}
                />

                {knmData.kindKnm && (
                  <>
                    <h4 className={styles.FeedbackFormLabel}>Вид КНМ</h4>
                    <TextInput
                      name="knmType"
                      disabled={true}
                      className={styles.FiledKnmGroup}
                      value={parseErknmValue<{ title?: string }>(knmData.kindKnm.value)?.title}
                    />
                  </>
                )}

                <h4 className={styles.FeedbackFormLabel}>Необходимость согласования</h4>
                <TextInput
                  name="approveRequired"
                  disabled={true}
                  className={styles.FiledKnmGroup}
                  value={knmData.approveRequired}
                />

                <h4 className={styles.FeedbackFormLabel}>Ссылка на паспорт КНМ</h4>
                <TextInput name="link" disabled={true} className={styles.FiledKnmGroup} value={getLink()} />

                <h4 className={styles.FeedbackFormLabel}>Пользователь</h4>
                <TextInput name="user" disabled={true} className={styles.FieldFullWidth} />
              </div>
            )}
            <TextInputField
              className={styles.FieldFullWidth}
              placeholder="Тема обращения"
              name="topic"
              validate={composeValidators(
                makeRequired('Поле не может быть пустым'),
                makeMaxCharactersValidator(1024, 'Превышена допустимая длина строки'),
              )}
              disabled={knmData !== undefined}
            />
            <TextareaField
              className={styles.FieldFullWidth}
              placeholder="Описание"
              name="text"
              validate={composeValidators(
                makeRequired('Поле не может быть пустым'),
                makeMaxCharactersValidator(10000, 'Превышена допустимая длина строки'),
              )}
            />
          </div>

          <UploadInput invalidExtensions={['xls', 'txt', 'rtf']} error={error} setError={setError} />
          <UploadStatus setError={setError} />
          <h3 className={styles.h3}>Общие требования по оформлению обращений в Службу технической поддержки</h3>
          <div className={styles.ListDesc}>
            При направлении обращений в Службу технической поддержки следует указывать следующий минимальный набор
            сведений:
          </div>
          <ul className={styles.List}>
            <li>ФИО и Роль пользователя в системе, под которым возникает ошибка;</li>
            <li>Полное наименование организации и региона РФ;</li>
            <li>Занимаемая должность в организации;</li>
            <li>Используемая система (ЕРП или ЕРКНМ);</li>
            <li>Номер паспорта проверки и/или плана проверок в системе;</li>
            <li>Пошаговое описание ситуации, приводящей к ошибке;</li>
            <li>Пошаговые иллюстрирующие скриншоты действий пользователя.</li>
          </ul>
          <div className={styles.ListDescLast}>
            А также иную дополнительную информацию, которая может более подробно описать Ваш вопрос.
          </div>
          <div className={styles.ListDesc}>
            Службой технической поддержки ФГИС &quot;Единый реестр контрольных (надзорный) мероприятий&quot; и ее
            подсистемы &quot;Единый реестр проверок&quot; не рассматриваются обращения, направленные через форму
            обратной связи или по электронной почте, в следующих случаях:
          </div>
          <ul className={styles.List}>
            <li>При отсутствии минимального набора сведений, указанных выше;</li>
            <li>При указании неполного либо недостоверного адреса электронной почты;</li>
            <li>
              Если предоставление ответа требует разглашения сведений, составляющих государственную, налоговую или иную
              охраняемую федеральным законом тайну;
            </li>
            <li>
              Если обращение не поддаётся прочтению, включая случаи, когда текст написан не на русском языке, с
              использованием латиницы, либо не структурирован (не разбит на предложения/ отсутствуют пробелы);
            </li>
            <li>Если обращение не содержит конкретных заявлений, жалоб или предложений.</li>
          </ul>
          <div className={styles.Group}>
            <CheckboxField
              name="agreement"
              label={
                <>
                  <span className={styles.AgreementText}>Я соглашаюсь на&nbsp;</span>
                  <span className={styles.AgreementButton} onClick={onModalOpen}>
                    обработку персональных данных
                  </span>
                </>
              }
              size="medium"
              type="checkbox"
              theme="system"
              validate={makeRequired('Пожалуйста, согласитесь с условиями')}
            />
          </div>
          <Button className={styles.SubmitButton} color="primary" type="submit" text="Отправить" />
        </form>
      </>
    );
  };

  return (
    <div className={styles.FeedbackForm}>
      <FeedbackAgreementModal onClose={onModalClose} visible={isAgreementOpen} />
      <Form<IFeedbackForm> onSubmit={onSubmitCallback} render={renderForm} subscription={{}} />
    </div>
  );
};

const mapStateToProps = (state: IRootState): IPublicFeedbackFormStoreProps => ({
  captchaToken: getCaptchaToken(state),
  knmData: getFeedbackKnmData(state),
});

export const PublicFeedbackForm = connect(mapStateToProps)(PublicFeedbackFormWithoutStore);
